import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import IntelexFrame from "../intelex/IntelexFrame";
import ReportField from "../intelex/ReportField";
import {
  airportCode,
  dataItem,
  dataList,
  displayDate,
  intelexDeleteImage,
  intelexRequest,
  intelexUploadImage,
  reportDetailPath,
} from "../intelex/api";

function parseSections(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
}

function fieldKey(field, sectionIndex, fieldIndex) {
  return String(
    field.id ||
      field.key ||
      `section_${sectionIndex + 1}_field_${fieldIndex + 1}`,
  );
}

function pirepLabel(report) {
  const origin = airportCode(report.departureAirport || report.origin) || "—";
  const destination =
    airportCode(report.arrivalAirport || report.destination) || "—";
  const flight =
    report.callsign || report.flightNumber || `PIREP #${report.id}`;
  return `${flight} · ${origin}–${destination} · ${displayDate(report.departureTime || report.createdAt, false)}`;
}

function ReportForm({ user }) {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [pireps, setPireps] = useState([]);
  const [answers, setAnswers] = useState({});
  const [pirepId, setPirepId] = useState("");
  const [pirepPage, setPirepPage] = useState({
    nextCursor: null,
    loading: false,
    error: "",
  });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({
    loading: true,
    saving: false,
    error: "",
  });
  const [dirty, setDirty] = useState(false);
  const testAccount = Boolean(user?.isTestAccount) || user?.role === "test";

  useEffect(() => {
    let current = true;
    setState({ loading: true, saving: false, error: "" });
    intelexRequest(
      `/api/portal/report-templates/${encodeURIComponent(templateId)}`,
    )
      .then(async (payload) => {
        if (!current) return;
        const nextTemplate = dataItem(payload);
        nextTemplate.sections = parseSections(nextTemplate.sections);
        setTemplate(nextTemplate);
        if (nextTemplate.requiresPirep && !testAccount) {
          try {
            const reportsPayload = await intelexRequest(
              "/api/portal/pireps?size=50",
            );
            if (current) {
              setPireps(dataList(reportsPayload));
              setPirepPage({
                nextCursor: reportsPayload.pagination?.nextCursor || null,
                loading: false,
                error: "",
              });
            }
          } catch (error) {
            if (current)
              setPirepPage({
                nextCursor: null,
                loading: false,
                error:
                  error.message || "Your PIREP history could not be loaded.",
              });
          }
        }
        if (current) setState({ loading: false, saving: false, error: "" });
      })
      .catch((error) => {
        if (current)
          setState({
            loading: false,
            saving: false,
            error: error.message || "This report form is unavailable.",
          });
      });
    return () => {
      current = false;
    };
  }, [templateId, testAccount]);

  useEffect(() => {
    const warn = (event) => {
      if (dirty && !state.saving) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, state.saving]);

  const sections = useMemo(() => parseSections(template?.sections), [template]);
  const selectedPirep = pireps.find(
    (report) => String(report.id) === String(pirepId),
  );

  const loadMorePireps = async (retry = false) => {
    const cursor = retry ? null : pirepPage.nextCursor;
    setPirepPage((current) => ({ ...current, loading: true, error: "" }));
    try {
      const payload = await intelexRequest(
        `/api/portal/pireps?size=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      );
      const incoming = dataList(payload);
      setPireps((current) => {
        const byId = new Map(
          (retry ? [] : current).map((report) => [String(report.id), report]),
        );
        incoming.forEach((report) => byId.set(String(report.id), report));
        return [...byId.values()];
      });
      setPirepPage({
        nextCursor: payload.pagination?.nextCursor || null,
        loading: false,
        error: "",
      });
    } catch (error) {
      setPirepPage((current) => ({
        ...current,
        loading: false,
        error: error.message || "More PIREPs could not be loaded.",
      }));
    }
  };

  const updateAnswer = (key, value) => {
    setDirty(true);
    setAnswers((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const validate = () => {
    const next = {};
    if (template.requiresPirep && !testAccount && !pirepId)
      next.pirepId = "Select the PIREP connected to this event.";
    sections.forEach((section, sectionIndex) =>
      (section.fields || []).forEach((field, fieldIndex) => {
        const key = fieldKey(field, sectionIndex, fieldIndex);
        const value = answers[key];
        const missing =
          field.type === "checkbox"
            ? value !== true
            : field.type === "image"
              ? !Array.isArray(value) || value.length === 0
              : value === undefined ||
                value === null ||
                String(value).trim() === "";
        if (field.required && missing) next[key] = "This field is required.";
      }),
    );
    setErrors(next);
    const firstError = Object.keys(next)[0];
    if (firstError)
      window.setTimeout(
        () =>
          document
            .getElementById(
              firstError === "pirepId"
                ? "intelex-pirep-select"
                : `report-field-${firstError.replace(/[^a-z0-9_-]/gi, "-")}`,
            )
            ?.focus(),
        0,
      );
    return !firstError;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setState((current) => ({ ...current, saving: true, error: "" }));
    try {
      const submittedAnswers = { ...answers };
      sections.forEach((section, sectionIndex) =>
        (section.fields || []).forEach((field, fieldIndex) => {
          if (field.type !== "image") return;
          const key = fieldKey(field, sectionIndex, fieldIndex);
          submittedAnswers[key] = (answers[key] || []).map(
            (attachment) => attachment.id,
          );
        }),
      );
      const payload = await intelexRequest("/api/portal/reports", {
        method: "POST",
        body: {
          templateId: template.id,
          pirepId:
            template.requiresPirep && !testAccount
              ? Number(pirepId) || pirepId
              : null,
          answers: submittedAnswers,
        },
      });
      const created = dataItem(payload);
      setDirty(false);
      navigate(
        created?.id ? reportDetailPath(created) : "/interep/reports",
        { replace: true, state: { submitted: true } },
      );
    } catch (error) {
      setState((current) => ({
        ...current,
        saving: false,
        error: error.message || "The report could not be submitted.",
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (state.loading)
    return (
      <div className="intelex-main">
        <div className="intelex-loading" role="status">
          Loading report form…
        </div>
      </div>
    );
  if (!template || state.error)
    return (
      <div className="intelex-main">
        <div className="intelex-alert" role="alert">
          {state.error || "This report form is unavailable."}
        </div>
        <Link className="intelex-secondary-button" to="/interep">
          Back to reports
        </Link>
      </div>
    );

  return (
    <div className="intelex-main intelex-report-form-page">
      <div className="intelex-breadcrumbs">
        <Link to="/interep">Safety reports</Link>
        <span aria-hidden="true">/</span>
        <span>{template.title}</span>
      </div>
      <header className="intelex-page-heading intelex-form-heading">
        <div>
          <h1>{template.title}</h1>
          {template.description && <p>{template.description}</p>}
        </div>
      </header>
      <section
        className="intelex-responder-identity"
        aria-label="Authenticated reporter"
      >
        <div>
          <span>Submitting as</span>
          <strong>{user.username || "Pilot"}</strong>
        </div>
        <div>
          <span>vAMSYS user</span>
          <strong>
            {user.vamsysUserId ? `#${user.vamsysUserId}` : "Local test account"}
          </strong>
        </div>
      </section>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}

      <form className="intelex-report-form" onSubmit={submit} noValidate>
        {template.requiresPirep && testAccount && (
          <section className="intelex-form-section intelex-test-pirep-bypass">
            <div className="intelex-form-section-heading">
              <span>01</span>
              <div>
                <h2>Related flight report</h2>
                <p>
                  PIREP selection is bypassed for this local test submission.
                  The report will be clearly marked as test data.
                </p>
              </div>
            </div>
          </section>
        )}
        {template.requiresPirep && !testAccount && (
          <section className="intelex-form-section intelex-pirep-section">
            <div className="intelex-form-section-heading">
              <span>01</span>
              <div>
                <h2>Related flight report</h2>
                <p>This report must be linked to one of your own PIREPs.</p>
              </div>
            </div>
            <div className="intelex-form-grid">
              <div
                className={`intelex-field intelex-width-12${errors.pirepId ? " has-error" : ""}`}
              >
                <label
                  className="intelex-field-label"
                  htmlFor="intelex-pirep-select"
                >
                  PIREP <span aria-hidden="true">*</span>
                </label>
                <select
                  id="intelex-pirep-select"
                  required
                  value={pirepId}
                  aria-invalid={Boolean(errors.pirepId)}
                  onChange={(event) => {
                    setPirepId(event.target.value);
                    setDirty(true);
                    setErrors((current) => ({ ...current, pirepId: "" }));
                  }}
                >
                  <option value="">
                    Select a flight from your PIREP history
                  </option>
                  {pireps.map((report) => (
                    <option value={report.id} key={report.id}>
                      {pirepLabel(report)}
                    </option>
                  ))}
                </select>
                {errors.pirepId && (
                  <p className="intelex-field-error" role="alert">
                    {errors.pirepId}
                  </p>
                )}
              </div>
            </div>
            {selectedPirep && (
              <div className="intelex-linked-pirep">
                <div>
                  <span>PIREP</span>
                  <strong>#{selectedPirep.id}</strong>
                </div>
                <div>
                  <span>Flight</span>
                  <strong>
                    {selectedPirep.callsign ||
                      selectedPirep.flightNumber ||
                      "—"}
                  </strong>
                </div>
                <div>
                  <span>Route</span>
                  <strong>
                    {airportCode(
                      selectedPirep.departureAirport || selectedPirep.origin,
                    ) || "—"}{" "}
                    →{" "}
                    {airportCode(
                      selectedPirep.arrivalAirport || selectedPirep.destination,
                    ) || "—"}
                  </strong>
                </div>
                <div>
                  <span>Date</span>
                  <strong>
                    {displayDate(
                      selectedPirep.departureTime || selectedPirep.createdAt,
                      false,
                    )}
                  </strong>
                </div>
              </div>
            )}
            {pirepPage.error && (
              <div className="intelex-inline-notice error" role="alert">
                {pirepPage.error}{" "}
                <button
                  type="button"
                  onClick={() => loadMorePireps(true)}
                  disabled={pirepPage.loading}
                >
                  {pirepPage.loading ? "Retrying…" : "Retry"}
                </button>
              </div>
            )}
            {!pireps.length && !pirepPage.error && (
              <div className="intelex-inline-notice">
                No PIREPs are available on your account. Submit the flight
                report before creating this report.
              </div>
            )}
            {pirepPage.nextCursor && (
              <div className="intelex-pirep-pagination">
                <span>{pireps.length} flights loaded</span>
                <button
                  type="button"
                  onClick={() => loadMorePireps(false)}
                  disabled={pirepPage.loading}
                >
                  {pirepPage.loading ? "Loading flights…" : "Load more flights"}
                </button>
              </div>
            )}
          </section>
        )}

        {sections.map((section, sectionIndex) => (
          <section
            className="intelex-form-section"
            key={section.id || sectionIndex}
          >
            <div className="intelex-form-section-heading">
              <span>
                {String(
                  sectionIndex + (template.requiresPirep ? 2 : 1),
                ).padStart(2, "0")}
              </span>
              <div>
                <h2>{section.title || `Section ${sectionIndex + 1}`}</h2>
                {section.description && <p>{section.description}</p>}
              </div>
            </div>
            <div className="intelex-form-grid">
              {(section.fields || []).map((field, fieldIndex) => {
                const key = fieldKey(field, sectionIndex, fieldIndex);
                return (
                  <ReportField
                    key={key}
                    field={{ ...field, id: key }}
                    value={answers[key]}
                    error={errors[key]}
                    onChange={(value) => updateAnswer(key, value)}
                    onUploadImage={(file) =>
                      intelexUploadImage(file, {
                        purpose: "answer",
                        fieldId: key,
                      })
                    }
                    onRemoveImage={(attachment) =>
                      intelexDeleteImage(attachment.id)
                    }
                  />
                );
              })}
            </div>
          </section>
        ))}

        <footer className="intelex-form-actions">
          <div>
            <strong>Ready to submit?</strong>
            <span>
              Review the report carefully. Submitted reports cannot be edited.
            </span>
          </div>
          <div>
            <Link className="intelex-text-button" to="/interep">
              Cancel
            </Link>
            <button
              className="intelex-primary-button"
              type="submit"
              disabled={
                state.saving ||
                (template.requiresPirep && !testAccount && !pireps.length)
              }
            >
              {state.saving ? "Submitting…" : "Submit report"}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}

export default function IntelexNewReportPage() {
  return (
    <IntelexFrame active="home" title="New safety report">
      {({ user }) => <ReportForm user={user} />}
    </IntelexFrame>
  );
}
